import type { EffectTimelineClip, SerializedEffect, VideoEffect } from './types';
import { createEffectFromClip } from './registry';
import { normalizeEffectType } from './effectRegistry';

export { EFFECT_TYPE_ALIASES, normalizeEffectType } from './effectRegistry';

/** Flatten parameters to numeric values for export JSON. */
function flattenParameters(
  clip: EffectTimelineClip,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(clip.parameters)) {
    if (typeof v === 'number') out[k] = v;
    else if (v && typeof v === 'object' && 'staticValue' in v && v.staticValue != null) {
      out[k] = v.staticValue;
    }
  }
  return out;
}

export function serializeEffectClip(clip: EffectTimelineClip): SerializedEffect {
  return {
    id: clip.id,
    type: clip.type,
    start: clip.startTime,
    end: clip.endTime,
    enabled: clip.enabled,
    parameters: flattenParameters(clip),
    keyframes: clip.keyframeTracks,
    seed: clip.seed,
  };
}

export function deserializeEffectClip(raw: SerializedEffect | Record<string, unknown>): EffectTimelineClip | null {
  const type = normalizeEffectType(String(raw.type || ''));
  if (!type) return null;
  const start = Number((raw as SerializedEffect).start ?? (raw as { startTime?: number }).startTime ?? 0);
  const end = Number((raw as SerializedEffect).end ?? (raw as { endTime?: number }).endTime ?? start + 5);
  const params = (raw as SerializedEffect).parameters || {};
  return {
    id: String((raw as SerializedEffect).id || `vfx-${type}-${Math.random().toString(36).slice(2, 9)}`),
    type,
    name: String((raw as { name?: string }).name || type),
    startTime: start,
    endTime: Math.max(start + 0.1, end),
    enabled: (raw as SerializedEffect).enabled !== false,
    parameters: { ...params },
    keyframeTracks: (raw as SerializedEffect).keyframes,
    seed: (raw as SerializedEffect).seed,
  };
}

export function serializeEffectTracks(tracks: EffectTimelineClip[][]): SerializedEffect[] {
  return (tracks || [])
    .flat()
    .filter(Boolean)
    .map(serializeEffectClip);
}

export function deserializeEffectTracks(effects: SerializedEffect[]): EffectTimelineClip[][] {
  const clips = (effects || [])
    .map(deserializeEffectClip)
    .filter((c): c is EffectTimelineClip => Boolean(c));
  return clips.length ? [clips] : [[]];
}

export function clipsToVideoEffects(clips: EffectTimelineClip[]): VideoEffect[] {
  return clips
    .map(createEffectFromClip)
    .filter((e): e is VideoEffect => Boolean(e));
}

/** Build API export payload for server-side VFX application. */
export function buildVfxExportPayload(tracks: EffectTimelineClip[][]): SerializedEffect[] {
  return serializeEffectTracks(tracks).filter((e) => e.enabled !== false);
}
